import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, XCircle } from 'lucide-react';

export default function Problem() {
  const problems = [
    {
      text: "You don't lose because of bad analysis",
      image: "https://images.unsplash.com/photo-1745509267699-1b1db256601e?auto=format&fit=crop&w=800&q=80",
      alt: "Trading chart with technical analysis"
    },
    {
      text: "Rules break when emotions rise",
      image: "https://images.unsplash.com/photo-1639917714192-557a6143f788?auto=format&fit=crop&w=800&q=80",
      alt: "Trader breaking rules under emotional pressure"
    },
    {
      text: "You trade alone, without a system that questions your decisions",
      image: "https://images.unsplash.com/photo-1722159475082-0a2331580de3?auto=format&fit=crop&w=800&q=80",
      alt: "Solo trading workspace without guidance"
    },
    {
      text: "Discipline fails when logic is overridden in real time",
      image: "https://images.unsplash.com/photo-1748439435495-722cc1728b7e?auto=format&fit=crop&w=800&q=80",
      alt: "Failed trading discipline on multiple monitors"
    }
  ];

  return (
    <section className="py-10 md:py-16 bg-white">
      <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.4 }}
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#337AF7] tracking-tight text-center mb-12"
            data-testid="problem-title"
          >
            Why Skilled Traders Still Aren't Profitable
          </motion.h2>
          
          <div className="grid gap-6 md:gap-8">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                data-testid={`problem-bullet-${index + 1}`}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md hover:border-slate-300 transition-all duration-200"
              >
                <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
                  {/* Image */}
                  <div className="md:col-span-2 relative h-48 md:h-40">
                    <img 
                      src={problem.image}
                      alt={problem.alt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/40 md:bg-gradient-to-r md:from-transparent md:to-white/40"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="md:col-span-3 flex items-center p-6 md:p-8">
                    <div className="flex items-start gap-4 w-full">
                      <XCircle className="h-7 w-7 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-base md:text-lg text-[#162959]">{problem.text}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8 text-center text-sm md:text-base text-[#162959]/60 italic"
          >
            This section should feel uncomfortable — in a good way.
          </motion.p>
        </div>
      </div>
    </section>
  );
}